import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  AnonDataState,
  DashboardBannerState,
} from 'features/productGrowth/types';

import {
  transformAnonBusinessDetails,
  transformAnonTeamMembers,
} from './PostSignup/GustoIntegration/utils';
import {
  createPayrollFirstPayDate,
  createUploadDocument,
  fetchAnonCompanyData,
  fetchDashboardBanner,
  SLICE_NAME,
} from './actions';

interface ProductGrowthState {
  isPending: boolean;
  error: Record<string, string | null>;
  success: Record<string, string | null>;
  uploadDocumentIsPending: boolean;
  uploadDocumentError: Record<string, string | null>;
  uploadDocumentSuccess: Record<string, string | null>;
  dashboardBanner: DashboardBannerState;
  anonData: AnonDataState;
}

export const initialState: ProductGrowthState = {
  isPending: false,
  error: {},
  success: {},
  uploadDocumentIsPending: false,
  uploadDocumentError: {},
  uploadDocumentSuccess: {},
  dashboardBanner: {
    isPending: false,
    error: undefined,
    data: undefined,
  },
  anonData: {
    url: '',
    extractionProcessId: '',
    created: '',
    expiresAt: '',
    loading: false,
    error: false,
    company: undefined,
    team: undefined,
  },
};

const slice = createSlice({
  name: SLICE_NAME,
  initialState,

  reducers: {
    setAnonData(state, action: PayloadAction<Partial<AnonDataState>>) {
      // Merge incoming data with existing anonData to preserve nested defaults
      state.anonData = {
        ...state.anonData,
        ...action.payload,
      };
    },
  },
  extraReducers: builder => {
    builder.addCase(
      createPayrollFirstPayDate.pending,
      (state: ProductGrowthState) => {
        state.isPending = true;
        state.error = {};
      }
    );

    builder.addCase(createPayrollFirstPayDate.fulfilled, (state, action) => {
      state.isPending = false;
      state.success = { message: action.payload };
      state.error = {};
    });

    builder.addCase(
      createPayrollFirstPayDate.rejected,
      (state: ProductGrowthState, action) => {
        state.isPending = false;
        state.error = { message: action.payload as string };
        state.success = {};
      }
    );

    builder.addCase(createUploadDocument.pending, state => {
      state.uploadDocumentIsPending = true;
      state.uploadDocumentError = {};
    });

    builder.addCase(createUploadDocument.fulfilled, (state, action) => {
      state.uploadDocumentIsPending = false;
      state.uploadDocumentSuccess = { message: action.payload };
      state.uploadDocumentError = {};
    });

    builder.addCase(createUploadDocument.rejected, (state, action) => {
      state.uploadDocumentIsPending = false;
      state.uploadDocumentError = { message: action.payload as string };
      state.uploadDocumentSuccess = {};
    });

    builder.addCase(fetchDashboardBanner.pending, state => {
      state.dashboardBanner.isPending = true;
      state.dashboardBanner.error = undefined;
      state.dashboardBanner.data = undefined;
    });

    builder.addCase(fetchDashboardBanner.fulfilled, (state, action) => {
      state.dashboardBanner.isPending = false;
      state.dashboardBanner.error = undefined;
      state.dashboardBanner.data = {
        bannerType: action.payload.bannerType,
        isDismissable: action.payload.isDismissable,
      };
    });

    builder.addCase(fetchDashboardBanner.rejected, (state, action) => {
      state.dashboardBanner.isPending = false;
      state.dashboardBanner.error = action.payload as string;
      state.dashboardBanner.data = undefined;
    });

    builder.addCase(fetchAnonCompanyData.pending, state => {
      state.anonData.loading = true;
      state.anonData.error = false;
      state.anonData.company = undefined;
      state.anonData.team = undefined;
    });
    builder.addCase(fetchAnonCompanyData.rejected, state => {
      state.anonData.loading = false;
      state.anonData.error = true;
      state.anonData.company = undefined;
      state.anonData.team = undefined;
    });
    builder.addCase(fetchAnonCompanyData.fulfilled, (state, action) => {
      state.anonData.loading = false;
      state.anonData.error = false;
      state.anonData.company = transformAnonBusinessDetails(action.payload);
      state.anonData.team = transformAnonTeamMembers(
        action.payload.employee_roster || []
      );
    });
  },
});

export const { actions, reducer } = slice;
